import { localStorageColorSchemeManager, MantineProvider } from "@mantine/core";
import { type ReactNode, useMemo } from "react";

import { buildTheme, type OrganizationBranding } from "./theme";

/**
 * ThemeProviderProps configure the Mantine provider wrapper. branding tints the
 * theme with the org palette. colorScheme selects how the rendered scheme is
 * resolved: "light"/"dark" pin a forced scheme (the app shell pins light, the
 * embed pins the host org's preferred scheme around its own subtree), while
 * "auto" lets the user pick and follows the system preference. colorSchemeKey is
 * the localStorage slot the "auto" choice persists to; each consumer supplies its
 * own scoped key so apps never collide. It is ignored under the forced modes.
 */
interface ThemeProviderProps {
  branding?: OrganizationBranding | null;
  children: ReactNode;
  colorScheme?: "light" | "dark" | "auto";
  colorSchemeKey?: string;
}

/**
 * ThemeProvider wraps Mantine's provider with the org-aware theme, shared by web
 * and docs. A sanctioned JSX carve-out: the theme/provider wrappers are the only
 * render-ful code permitted in this otherwise render-free package set.
 *
 * Two modes resolve the rendered scheme:
 *   - "light"/"dark" force the scheme and construct no color-scheme manager, so
 *     the choice never follows the system and never persists. The app shell pins
 *     "light"; the embed pins the org's resolved scheme to override that default
 *     for its subtree.
 *   - "auto" defaults to the system preference and is user-controlled: the choice
 *     persists to localStorage under colorSchemeKey, scoped per consumer so two
 *     apps on the same origin never overwrite each other's slot.
 */
export const ThemeProvider = ({
  branding = null,
  children,
  colorScheme = "light",
  colorSchemeKey,
}: ThemeProviderProps) => {
  // Rebuild the theme only when the org branding changes (the consumers pass a stable
  // branding reference), so an org-aware re-render high in the tree does not rebuild the
  // full component-override theme — and re-tint the app — on every render.
  const theme = useMemo(() => buildTheme(branding), [branding]);

  if (colorScheme === "auto") {
    return (
      <MantineProvider
        colorSchemeManager={localStorageColorSchemeManager({ key: colorSchemeKey })}
        defaultColorScheme="auto"
        theme={theme}
      >
        {children}
      </MantineProvider>
    );
  }

  return (
    <MantineProvider forceColorScheme={colorScheme} theme={theme}>
      {children}
    </MantineProvider>
  );
};
